import { ChainType, type WalletGroupedAccounts } from '@polaris/core/models'
import { shorten } from '@polaris/core/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { useAtomValue } from 'jotai'
import { Binoculars } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useValidAddressInput } from '@/src/hooks/accounts/use-valid-address-input'
import { useAccountsActions } from '@/src/stores/accounts/actions'
import { walletAccountsAtom } from '@/src/stores/accounts/wallets'
import { cn } from '@/src/utils/styles'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../ui/dialog'
import { Input } from '../ui/input'
import { AccountCard } from './account-card'
import { WalletAddedSuccess } from './detected-pending-account'

export const WatchWalletAddressScreen = ({
  onClose,
}: {
  onClose?: () => void
}) => {
  const { groupedAccounts, accountsWithWallets } =
    useAtomValue(walletAccountsAtom)
  const excludeAddresses = useMemo(
    () => accountsWithWallets.map((a) => a.address),
    [accountsWithWallets],
  )
  const {
    address,
    isValid,
    isUnique,
    loading,
    detectedChainType,
    handleChange,
    setAddress,
  } = useValidAddressInput({
    excludeAddresses,
    // Context: https://linear.app/osmosis/issue/POLFE-1121/remove-bitcoin-connector
    // We want to allow users ability to watch BTC addresses despite no trading support.
    supplementaryChainTypes: [ChainType.Bitcoin],
  })
  const { addWatchAddress, addAccounts } = useAccountsActions()
  const [addSuccess, setAddSuccess] = useState(false)

  // Once the address is added, it will begin to match and we will switch to the success screen.
  const addressAccount = groupedAccounts.find(({ accounts }) =>
    accounts.some((a) => a.address === address),
  )

  const accountFixture = useMemo<WalletGroupedAccounts>(() => {
    const baseAccountFixture: WalletGroupedAccounts = {
      name: '',
      provider_info: {
        name: '',
        img: '',
      },
      type: 'WatchAddress',
      walletConnectionStatus: 'none' as const,
      totalValue: 0,
      grouping_key: '',
      balances: [],
      accounts: [],
    }

    if (isValid && address) {
      return {
        ...baseAccountFixture,
        name: shorten(address),
      } as WalletGroupedAccounts
    }

    // Empty placeholder account during user input
    return baseAccountFixture
  }, [isValid, address])

  if (addressAccount && addSuccess) {
    return (
      <div className='max-w-md px-8 flex flex-col items-center gap-4'>
        <WalletAddedSuccess
          className='w-full'
          successAccount={addressAccount}
          onClose={onClose}
        />
      </div>
    )
  }

  const isAddressEmpty = address.trim().length === 0

  const isDisabled =
    addSuccess ||
    addAccounts.isPending ||
    (!isAddressEmpty && (isValid !== true || loading || !detectedChainType))

  return (
    <div className='max-w-md px-8 flex flex-col items-center space-y-8 pb-8'>
      <div>
        <h1 className='text-center text-primary text-4xl font-extralight font-serif flex-col'>
          Watch your address
        </h1>
        <p className='text-center text-secondary'>
          Add a view-only wallet address to your portfolio to track its balances
          across all networks.
        </p>
      </div>

      <AccountCard
        account={accountFixture}
        size='lg'
        totalValueReplaceContent={
          accountFixture.totalValue === 0 ? '' : undefined
        }
      />

      <div className='w-full flex flex-col gap-1'>
        {!isUnique && (
          <span className='text-danger-text text-sm'>
            This address is already in your portfolio.
          </span>
        )}
        <Input
          placeholder='Enter a wallet address'
          dimension='large'
          type='text'
          autoFocus
          value={address}
          onChange={handleChange}
          isValid={isValid}
          isInvalid={isValid === false}
        />
      </div>
      <Button
        variant={isAddressEmpty ? 'secondary' : 'primary'}
        className='w-full'
        disabled={isDisabled}
        onClick={async () => {
          if (isAddressEmpty) {
            try {
              const text = await navigator.clipboard.readText()
              const candidate = text.trim()
              if (candidate) {
                setAddress(candidate)
              }
            } catch (err) {
              console.error('Failed to read from clipboard', err)
            }
            return
          }

          if (!detectedChainType) {
            console.error('No detected chain type when adding watch address')
            return
          }

          const watchAddressName = shorten(address)
          addWatchAddress(address, detectedChainType, watchAddressName).then(
            () => setAddSuccess(true),
          )
        }}
      >
        {isAddressEmpty ? 'Paste from clipboard' : 'Add wallet'}
      </Button>
    </div>
  )
}

const watchAddressIconVariants = cva(
  'self-start flex items-center justify-center bg-[#7DB396] rounded-md',
  {
    variants: {
      size: {
        xs: 'size-6 min-w-6',
        sm: 'size-8 min-w-8',
        md: 'size-10 min-w-10',
        lg: 'size-12 min-w-12',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

interface WatchAddressIconProps
  extends VariantProps<typeof watchAddressIconVariants> {
  disabled?: boolean
  className?: string
}

export const WatchAddressIcon = ({
  disabled = false,
  size,
  className,
}: WatchAddressIconProps) => {
  const iconPixelSize =
    size === 'xs' ? 16 : size === 'sm' ? 20 : size === 'lg' ? 40 : 32

  return (
    <span
      className={cn(
        watchAddressIconVariants({ size }),
        { 'opacity-50': disabled },
        className,
      )}
    >
      <Binoculars size={iconPixelSize} className='text-green-800' />
    </span>
  )
}

export function WatchWalletAddressDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className='sm:max-w-[28rem] min-h-[unset] h-fit pt-0'
        showCloseButton
      >
        <DialogTitle className='hidden' />
        <DialogDescription className='hidden' />
        <WatchWalletAddressScreen onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}
